import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Loader2, ShieldAlert, Trash2, PauseCircle, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import { useIsAdmin } from "@/hooks/useUserRole";
import { fetchSensitiveDocs, purgeLeads, setHold, purgeStatus, fmtSAST, PURGE_GRACE_DAYS, type SensitiveDocRow } from "@/lib/sensitiveDocPurge";

export function PurgeStatusBadge({ row }: { row: SensitiveDocRow }) {
  const s = purgeStatus(row);
  if (s === "purged") return <Badge variant="secondary">Purged</Badge>;
  if (s === "on_hold") return <Badge variant="outline">On hold</Badge>;
  if (s === "eligible") return <Badge variant="destructive">Due for deletion</Badge>;
  if (s === "no_end_date") return <Badge variant="outline">No end date yet</Badge>;
  return <Badge variant="outline">Kept until {fmtSAST(row.purge_due)}</Badge>;
}

/** Type-to-confirm dialog shared by the lead panel and the admin bulk page. */
export function ConfirmPurgeDialog({ open, onOpenChange, confirmWord, summary, onConfirm, busy }: {
  open: boolean; onOpenChange: (o: boolean) => void; confirmWord: string; summary: string; onConfirm: () => void; busy: boolean;
}) {
  const [typed, setTyped] = useState("");
  return (
    <AlertDialog open={open} onOpenChange={(o) => { if (!o) setTyped(""); onOpenChange(o); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Permanently delete ID & passport files?</AlertDialogTitle>
          <AlertDialogDescription>
            {summary} This removes the photos from student and client intake, the copies in Documents, any share links, and passport numbers. It cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-1">
          <p className="text-sm">Type <span className="font-mono font-semibold">{confirmWord}</span> to confirm.</p>
          <Input value={typed} onChange={(e) => setTyped(e.target.value)} autoFocus />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
          <Button variant="destructive" disabled={busy || typed.trim() !== confirmWord} onClick={onConfirm}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Delete permanently
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function SensitiveDocsPanel({ leadId }: { leadId: string }) {
  const qc = useQueryClient();
  const { isAdmin } = useIsAdmin();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [holdOpen, setHoldOpen] = useState(false);
  const [reason, setReason] = useState("");

  const q = useQuery({ queryKey: ["sensitive-docs", leadId], queryFn: () => fetchSensitiveDocs(leadId) });
  const row = q.data?.[0];
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["sensitive-docs"] });
    qc.invalidateQueries({ queryKey: ["student-intake-subs"] });
    qc.invalidateQueries({ queryKey: ["unified-activity-feed"] });
  };

  const purge = useMutation({
    mutationFn: () => purgeLeads([leadId]),
    onSuccess: () => { toast.success("ID and passport files deleted"); setConfirmOpen(false); refresh(); },
    onError: (e: any) => toast.error(e.message || "Purge failed"),
  });
  const hold = useMutation({
    mutationFn: (on: boolean) => setHold(leadId, on, reason),
    onSuccess: () => { setHoldOpen(false); setReason(""); refresh(); },
    onError: (e: any) => toast.error(e.message || "Could not update hold"),
  });

  if (!row) return null;
  const status = purgeStatus(row);
  const total = row.files_count + row.vault_docs_count;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex flex-wrap items-center gap-2">
          <ShieldAlert className="h-4 w-4" /> Sensitive documents <PurgeStatusBadge row={row} />
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {status === "purged" ? (
          <p className="text-muted-foreground">No ID or passport files are stored{row.last_purged_at ? ` — deleted on ${fmtSAST(row.last_purged_at)}` : ""}.</p>
        ) : (
          <>
            <p>
              {row.submissions_with_docs} participant{row.submissions_with_docs === 1 ? "" : "s"} · {total} ID/passport file{total === 1 ? "" : "s"} (including Documents copies).
            </p>
            <p className="text-muted-foreground">
              {status === "on_hold" && <>On hold: {row.hold_reason}. Nothing will be deleted until the hold is removed.</>}
              {status === "waiting" && <>Deleted automatically on {fmtSAST(row.purge_due)}, {PURGE_GRACE_DAYS} days after the programme ends.</>}
              {status === "eligible" && <>The {PURGE_GRACE_DAYS}-day grace period has passed. These files will be deleted automatically tonight, or you can delete them now.</>}
              {status === "no_end_date" && <>Deleted automatically {PURGE_GRACE_DAYS} days after the programme ends, once it has dates.</>}
            </p>
            {isAdmin && (
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="destructive" disabled={!row.eligible} onClick={() => setConfirmOpen(true)}>
                  <Trash2 className="h-4 w-4" /> Purge all ID & passport files
                </Button>
                {row.on_hold ? (
                  <Button size="sm" variant="outline" onClick={() => hold.mutate(false)} disabled={hold.isPending}>
                    <PlayCircle className="h-4 w-4" /> Remove hold
                  </Button>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => setHoldOpen(true)}>
                    <PauseCircle className="h-4 w-4" /> Keep for now
                  </Button>
                )}
              </div>
            )}
          </>
        )}
      </CardContent>

      <ConfirmPurgeDialog open={confirmOpen} onOpenChange={setConfirmOpen} confirmWord="DELETE" busy={purge.isPending}
        summary={`${total} file(s) for ${row.lead_title ?? "this lead"} will be deleted.`} onConfirm={() => purge.mutate()} />

      <AlertDialog open={holdOpen} onOpenChange={setHoldOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Keep ID & passport files for now?</AlertDialogTitle>
            <AlertDialogDescription>Automatic deletion is paused until you remove the hold. Give a reason, e.g. "visa dispute".</AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason" />
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button disabled={!reason.trim() || hold.isPending} onClick={() => hold.mutate(true)}>Put on hold</Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
